// Shared layout + AccessGuard for /7days/dashboard/* pages
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { LogOut, Lock, ChevronRight, Home } from "lucide-react";
import { api } from "../../lib/api";
import { ensureFonts, FONT_AR, FONT_DISPLAY, getStored, setStored } from "../../lib/sevenTheme";
import { ACCESS, BUNDLES } from "../../data/sevenNights";

export const useSevenAccess = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState({ loading: true, bundle: null, token: null });

  useEffect(() => {
    ensureFonts();
    const stored = getStored();
    const tokenFromUrl = params.get("t");
    const token = tokenFromUrl || stored.token;
    if (!token) {
      navigate("/7days/checkout?bundle=complete");
      return;
    }
    // Validate token with backend (silent fallback to stored bundle if API fails)
    (async () => {
      try {
        const { data } = await api.get(`/seven/order/${token}`);
        if (data?.status === "paid") {
          setStored({ token, bundle: data.bundle, email: data.email });
          setState({ loading: false, bundle: data.bundle, token, email: data.email });
        } else if (stored.bundle) {
          setState({ loading: false, bundle: stored.bundle, token: stored.token });
        } else {
          navigate("/7days/checkout?bundle=complete");
        }
      } catch {
        if (stored.token && stored.bundle) {
          setState({ loading: false, bundle: stored.bundle, token: stored.token });
        } else {
          navigate("/7days/checkout?bundle=complete");
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
};

export const SevenShell = ({ children, title, subtitle, testid }) => {
  const navigate = useNavigate();
  const logout = () => {
    try { localStorage.removeItem("seven_nights_v1"); } catch (_) { /* private mode */ }
    navigate("/7days");
  };
  return (
    <div dir="rtl" lang="ar" className="min-h-screen bg-[#0A0606] text-[#F5EAD3]" style={{ fontFamily: FONT_AR }} data-testid={testid}>
      <nav className="sticky top-0 z-30 bg-[#0A0606]/85 backdrop-blur-md border-b border-white/[0.05]">
        <div className="max-w-5xl mx-auto px-5 py-3.5 flex items-center justify-between gap-3">
          <Link to="/7days/dashboard" className="flex items-center gap-2.5">
            <img src="/images/7nights-logo.png" alt="" className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <div className="font-extrabold text-[14.5px] text-white" style={{ fontFamily: FONT_DISPLAY }}>7 ليالي بيننا</div>
              <div className="text-[10px] text-[#A7956E] tracking-[0.16em]">{subtitle || "لوحة التجربة"}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/7days/dashboard" className="text-[12.5px] text-[#CFC3A1] hover:text-[#C9A66A] flex items-center gap-1.5 px-3 py-2 rounded-sm">
              <Home className="w-3.5 h-3.5" /> الرئيسية
            </Link>
            <button onClick={logout} data-testid="seven-logout" className="text-[12.5px] text-[#CFC3A1] hover:text-[#F0838F] flex items-center gap-1.5 px-3 py-2 rounded-sm">
              <LogOut className="w-3.5 h-3.5" /> خروج
            </button>
          </div>
        </div>
      </nav>
      <main className="px-5 py-8 sm:py-12 max-w-5xl mx-auto">
        {title && (
          <header className="mb-9 text-center sm:text-right">
            <h1 className="font-extrabold text-[28px] sm:text-[36px] leading-[1.25] text-white" style={{ fontFamily: FONT_DISPLAY }}>{title}</h1>
          </header>
        )}
        {children}
      </main>
      <footer className="py-9 text-center px-5">
        <p className="text-[11px] text-[#7A6F50] max-w-lg mx-auto leading-relaxed">
          هذا البرنامج خاص بكِ. لا يتم حفظ إجاباتك داخل الموقع.
        </p>
      </footer>
    </div>
  );
};

// Dashboard home — list of available sections per bundle
export default function DashboardSeven() {
  const { loading, bundle } = useSevenAccess();
  if (loading) return <div className="min-h-screen bg-[#0A0606] flex items-center justify-center text-[#A7956E]" style={{ fontFamily: FONT_AR }}>جاري التحميل…</div>;
  const access = ACCESS[bundle] || ACCESS.starter;
  const bundleInfo = BUNDLES.find((b) => b.key === bundle) || BUNDLES[0];

  const cards = [
    { key: "newlyweds", href: "/7days/dashboard/newlyweds", title: "برنامج العرسان", desc: "أول 7 ليالي للعرسان — بداية مريحة، بدون ضغط.", show: access.newlyweds },
    { key: "routine", href: "/7days/dashboard/routine-reset", title: "برنامج بعد الروتين", desc: "7 ليالي لإعادة القرب، الضحك، والاهتمام.", show: access.routine },
    { key: "cards", href: "/7days/dashboard/cards", title: "مكتبة البطاقات", desc: `${access.cards} بطاقة رقمية مرتبة بفئات.`, show: true },
    { key: "scripts", href: "/7days/dashboard/scripts", title: "رسائل واتساب جاهزة", desc: "رسائل قصيرة محترمة جاهزة للنسخ والإرسال.", show: access.scripts },
    { key: "bonus", href: "/7days/dashboard/bonus", title: "أفكار ليالي بيتية + خطة شهرية", desc: "10 أفكار ليالي + 4 أسابيع للاستمرار.", show: access.bonus },
  ];

  return (
    <SevenShell title="أهلًا بكِ في «7 ليالي بيننا»." testid="seven-dashboard-root">
      <p className="text-center sm:text-right text-[14.5px] text-[#CFC3A1] leading-[1.95] mb-9 max-w-2xl">
        اختاري البرنامج المناسب وابدئي بهدوء. لا تستعجلي. ليلة واحدة في كل مرة تكفي.
      </p>
      <div className="inline-flex items-center gap-2 mb-7 text-[11px] tracking-[0.22em] text-[#C9A66A] font-bold bg-[#1C1010]/60 border border-[#C9A66A]/25 rounded-full px-4 py-1.5">
        <Lock className="w-3 h-3" /> باقتك: {bundleInfo.label}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {cards.filter((c) => c.show).map((c) => (
          <Link key={c.key} to={c.href} data-testid={`seven-dashboard-card-${c.key}`} className="group bg-gradient-to-b from-[#1C1010] to-[#150C0C] border border-[#C9A66A]/22 hover:border-[#C9A66A]/55 rounded-2xl p-6 transition-all flex items-center justify-between gap-4">
            <div className="flex-1">
              <div className="font-bold text-[17px] text-white mb-1.5" style={{ fontFamily: FONT_DISPLAY }}>{c.title}</div>
              <div className="text-[13px] text-[#CFC3A1] leading-[1.7]">{c.desc}</div>
            </div>
            <ChevronRight className="w-5 h-5 text-[#C9A66A] rotate-180 group-hover:-translate-x-1 transition-transform shrink-0" />
          </Link>
        ))}
      </div>
    </SevenShell>
  );
}
